import { motion } from 'framer-motion';
import { Target, Eye, Heart, Shield, Users, Award } from 'lucide-react';
import SectionHeading from '../components/SectionHeading';

const values = [
  {
    icon: Heart,
    title: 'Compassion',
    description: 'We serve all community members with empathy and understanding.',
  },
  {
    icon: Shield,
    title: 'Integrity',
    description: 'We maintain the highest standards of honesty and accountability.',
  },
  {
    icon: Users,
    title: 'Unity',
    description: 'We bring together diverse Muslims under one common purpose.',
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'We strive for excellence in every program and service we offer.',
  },
];

const AboutPage = () => {
  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Building a Stronger{' '}
              <span className="text-gradient">Muslim Community</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              True North Muslim Community is a Canadian not-for-profit organization dedicated to 
              strengthening faith, serving the community, and supporting orphans and vulnerable families 
              across Barrie and Simcoe County.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 lg:py-32" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="inline-flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <h2 className="text-2xl font-semibold font-['Outfit'] text-foreground">Our Mission</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                To provide comprehensive support to orphans and vulnerable families while fostering 
                a vibrant Muslim community through religious services, charitable programs, and 
                community building initiatives.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-muted-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  Support orphans with food, clothing, education, and shelter
                </li>
                <li className="flex items-start gap-2 text-muted-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  Provide weekly Jummah prayers and religious education
                </li>
                <li className="flex items-start gap-2 text-muted-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  Assist vulnerable families with essential needs
                </li>
                <li className="flex items-start gap-2 text-muted-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  Build bridges within and beyond the Muslim community
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="inline-flex items-center gap-3">
                <div className="p-3 rounded-xl bg-secondary/10">
                  <Eye className="h-6 w-6 text-secondary" />
                </div>
                <h2 className="text-2xl font-semibold font-['Outfit'] text-foreground">Our Vision</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                To be the leading Muslim charitable organization in Simcoe County, known for our 
                unwavering commitment to orphan care, community service, and transparent governance.
              </p>
              <div className="p-6 rounded-2xl bg-card border border-border">
                <blockquote className="text-lg italic text-foreground">
                  "The one who looks after an orphan, whether he is his relative or not, will be with 
                  me in Paradise like these two..."
                </blockquote>
                <p className="mt-3 text-sm text-muted-foreground">— Prophet Muhammad (PBUH)</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 lg:py-32 bg-card islamic-pattern" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Core Values"
            title="What We Stand For"
            description="Our values guide every decision we make and every service we provide."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-background border border-border hover:border-primary/30 transition-colors"
                data-testid={`value-${value.title.toLowerCase()}`}
              >
                <div className="p-3 rounded-xl bg-primary/10 w-fit mb-4">
                  <value.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold font-['Outfit'] text-foreground mb-2">{value.title}</h3>
                <p className="text-sm text-muted-foreground">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Transparency */}
      <section className="py-24 lg:py-32" data-testid="transparency-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <SectionHeading
              badge="Transparency"
              title="Accountability First"
              description="We believe in complete transparency in all our operations and financial management."
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-2xl bg-card border border-border">
                <h4 className="font-semibold font-['Outfit'] text-foreground mb-2">CRA Aligned</h4>
                <p className="text-sm text-muted-foreground">All programs follow CRA charitable guidelines</p>
              </div>
              <div className="p-6 rounded-2xl bg-card border border-border">
                <h4 className="font-semibold font-['Outfit'] text-foreground mb-2">Open Books</h4>
                <p className="text-sm text-muted-foreground">Financial reports available upon request</p>
              </div>
              <div className="p-6 rounded-2xl bg-card border border-border">
                <h4 className="font-semibold font-['Outfit'] text-foreground mb-2">Clear Policies</h4>
                <p className="text-sm text-muted-foreground">Documented governance and conflict policies</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
